// HU-27 · HU-25 · «Disposiciones y seguimiento» (`ui/MG-ILE-55.md`, reglas de registro en
// `ui/MG-ILE-53.md`). Sprint 3.
//
// LA LISTA SE RECORRE POR COMPROMISO, NO POR CASO (AF §7f). Una sola lista con las acciones
// del Comité y las disposiciones de la autoridad del ámbito del usuario, ORDENADA POR EL
// SERVIDOR (lo que vence antes, primero; los vencidos arriba, HU-27-C2). Las «Cumplida» se
// ocultan por defecto y se ven con «Ver cumplidas» (decisión del operador, 29-sep).
//
// LA PANTALLA NO DECIDE NADA (§8.1). Cada fila trae su texto de clase, de estado y de
// vencimiento, quién la registró, y sus permisos (`puede_cambiar_estado`,
// `estados_siguientes`, `puede_revertir`). El selector de estado ofrece solo lo que el servidor
// dice; el Representante del País ve la insignia sin control (HU-27-C4). Tras cada cambio se
// relee la lista entera: el PATCH devuelve el estado del cierre, no la fila (§8.1 R3), y los
// contadores también cambian.
//
// PAGINA EL SERVIDOR, de 10 en 10 (operador, 5-oct-2026: «los listados paginan de 10 en 10»):
// la pantalla pide una página y pinta «Mostrando 1 a 10 de N compromisos.»; cualquier cambio de
// filtro vuelve a la página 1. Por eso «Vencidos» (`solo_vencidos`) y «Ver compromisos» del
// expediente (`caso_id`) también los aplica el servidor, antes de paginar.
//
// LO QUE EL CONTRATO TODAVÍA NO TRAE (vacíos anotados, no se inventa):
// · `acciones_permitidas` de la lista para «Registrar disposición»: sigue el rol de la sesión;
// · quién cierra: «Cerrar el caso» por fila sale cuando `compromiso.caso_puede_cerrarse` es
//   verdadero (el servidor evaluó las condiciones del cierre), pero el permiso de cerrar sigue el
//   rol (Comité y AR, como «Cerrar caso» del Expediente: no existe `cierre.acciones_permitidas`).
//   No cierra desde aquí: abre el Expediente del caso, donde está la tarjeta «Cierre del caso».

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { AlertTriangle, FilePlus2, FolderOpen, ListChecks, Lock, RotateCcw, RotateCw, ShieldAlert, X } from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, Codigo, DataTable, EmptyState, Field, KpiCard, Modal, Mono, PageHeader, Radio, Select,
  StatusBadge, Textarea,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla, OpcionDeSelect } from '../components/ui';
import SelectorDePais from '../components/ui/SelectorDePais';
import FiltrosColapsables from '../components/ui/FiltrosColapsables';
import Paginacion, { POR_PAGINA_DE_LISTADOS, paginasDe } from '../components/ui/Paginacion';
import RegistrarDisposicion from '../components/RegistrarDisposicion';
import { ROLES_QUE_CIERRAN } from '../components/CierreDelCaso';
import type { DisposicionRegistrada } from '../components/RegistrarDisposicion';
import { cambiarEstadoDeCompromiso, listarCompromisos } from '../api/regulatorio';
import type { Compromiso, FiltrosDeCompromisos, ListaDeCompromisos } from '../api/regulatorio';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { comoFecha } from '../lib/fechas';
import { etiquetaDelCompromiso, tonoDeLaClase, tonoDelCompromiso } from '../lib/etiquetas';
import { filtrosIniciales, guardarFiltros } from '../lib/filtrosPersistidos';
import { useCatalogosOpcional } from '../lib/CatalogosContexto';
import { tieneRol, usuarioActual } from '../lib/sesion';

/** Quién registra una disposición (HU-25-C1, C2; espejo de `SOLO_NOTIFICACION`). */
const ROLES_QUE_REGISTRAN = ['asuntos_regulatorios', 'representante_pais'];
const VENCIDOS = 'vencidos';

type Filtros = { clase: string; estado: string; ver_cumplidas: string; pais: string };
const SIN_FILTROS: Filtros = { clase: '', estado: '', ver_cumplidas: '', pais: '' };

/** Valores de los filtros fijados por la spec (HU-27-C6). */
const CLASES: OpcionDeSelect[] = [
  { value: '', label: 'Todas' },
  { value: 'accion', label: 'Solo acciones' },
  { value: 'disposicion', label: 'Solo disposiciones' },
];
const ESTADOS: OpcionDeSelect[] = [
  { value: '', label: 'Todos' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_curso', label: 'En curso' },
  { value: VENCIDOS, label: 'Vencidos' },
];
// «Todos» es un valor elegible (`sinVacio`): va antes de los países, que llevan su bandera.
const TODOS_LOS_PAISES: OpcionDeSelect[] = [{ value: '', label: 'Todos los de tu ámbito' }];

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'caso', label: 'Caso', testid: 'seguimiento-fila-caso' },
  { key: 'clase', label: 'Clase', testid: 'seguimiento-fila-clase' },
  { key: 'que', label: 'Qué se comprometió', className: 'min-w-[16rem]', testid: 'seguimiento-fila-que' },
  { key: 'responsable', label: 'Responsable', testid: 'seguimiento-fila-responsable' },
  { key: 'plazo', label: 'Para cuándo', testid: 'seguimiento-fila-plazo' },
  { key: 'estado', label: 'Estado', testid: 'seguimiento-fila-estado' },
];

/** Lo que se pide al servidor: los filtros de la pantalla, el caso (si viene del expediente) y la página. */
const aConsulta = (f: Filtros, casoId: string | null, pagina: number): FiltrosDeCompromisos => ({
  clase: (f.clase || '') as FiltrosDeCompromisos['clase'],
  estado: (f.estado && f.estado !== VENCIDOS ? f.estado : '') as FiltrosDeCompromisos['estado'],
  pais: f.pais || undefined,
  ver_cumplidas: f.ver_cumplidas === 'si',
  solo_vencidos: f.estado === VENCIDOS,
  caso_id: casoId ?? undefined,
  pagina,
  por_pagina: POR_PAGINA_DE_LISTADOS,
});

/** El aviso de éxito: el caso y el estado en que quedó, con el glosario. */
type Exito = { titulo: string; texto?: string | null; tono?: 'success' | 'warning' };

/** «Revertir» una «Cumplida» (decisión del 29-sep, HU-27-C17): motivo obligatorio y el estado al que vuelve. */
function ModalDeReversion({ compromiso, onCerrar, onRevertida }: {
  compromiso: Compromiso | null; onCerrar: () => void; onRevertida: (c: Compromiso, estado: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [estado, setEstado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const grupo = useId();

  useEffect(() => {
    setMotivo('');
    setEstado(compromiso?.estados_siguientes[0] ?? '');
    setFallo(null);
  }, [compromiso]);

  const revertir = async () => {
    if (!compromiso || !motivo.trim() || !estado) return;
    setOcupado(true);
    setFallo(null);
    try {
      await cambiarEstadoDeCompromiso(compromiso.caso_id, compromiso.compromiso_id, { estado, motivo: motivo.trim() });
      onRevertida(compromiso, estado);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Modal open={Boolean(compromiso)} onClose={onCerrar} tone="warning" icon={RotateCcw} sucio={Boolean(motivo.trim())}
      eyebrow="Disposiciones y seguimiento" title="Revertir «Cumplida»" data-testid="seguimiento-revertir-form"
      footer={<>
        <Button variant="secondary" onClick={onCerrar} data-testid="seguimiento-revertir-form-cancelar">Cancelar</Button>
        <Button icon={RotateCcw} onClick={() => { revertir(); }} disabled={ocupado || !motivo.trim() || !estado}
          data-testid="seguimiento-revertir-form-guardar">
          {ocupado ? 'Revirtiendo…' : 'Revertir'}
        </Button>
      </>}>
      {compromiso && <p className="text-body mb-3"><Mono>{compromiso.numero_de_caso}</Mono> · {compromiso.descripcion}</p>}
      {fallo && (
        <div className="mb-3">
          <Alert tone="warning" title="No se revirtió" detalleTecnico={fallo.detalleTecnico} data-testid="seguimiento-error-envio">
            {fallo.message}
          </Alert>
        </div>
      )}
      <Field label="Motivo" required hint="Queda en la pista de auditoría con quién lo revirtió y cuándo.">
        <Textarea rows={3} maxLength={1000} value={motivo} data-testid="seguimiento-revertir-form-motivo"
          onChange={(e) => setMotivo(e.target.value)} />
      </Field>
      {(compromiso?.estados_siguientes.length ?? 0) > 1 && (
        <Field label="Vuelve a" grupo required className="mt-2">
          <div className="flex flex-wrap gap-2">
            {compromiso?.estados_siguientes.map((e) => (
              <Radio key={e} name={grupo} value={e} label={etiquetaDelCompromiso(e)} checked={estado === e} onChange={() => setEstado(e)} />
            ))}
          </div>
        </Field>
      )}
    </Modal>
  );
}

/**
 * La columna «Caso»: el número abre el expediente; si el caso puede cerrarse y la sesión cierra,
 * se añade «Cerrar el caso» (ui/MG-ILE-55), que lleva a la tarjeta «Cierre del caso» del
 * Expediente, que muestra todas las condiciones; no cierra desde aquí.
 */
function celdaDelCaso(c: Compromiso, cierra: boolean, onAbrirCaso: ((casoId: string) => void) | undefined) {
  const numero = onAbrirCaso
    ? (
      <Button size="sm" variant="ghost" icon={FolderOpen} onClick={() => onAbrirCaso(c.caso_id)} className="whitespace-nowrap"
        aria-label={`Abrir el expediente de ${c.numero_de_caso}`}>
        <Codigo className="whitespace-nowrap">{c.numero_de_caso}</Codigo>
      </Button>
    )
    : <Codigo className="whitespace-nowrap">{c.numero_de_caso}</Codigo>;
  if (!(c.caso_puede_cerrarse && cierra && onAbrirCaso)) return numero;
  return (
    <span className="inline-flex flex-col items-start gap-1">
      {numero}
      <Button size="sm" variant="secondary" icon={Lock} onClick={() => onAbrirCaso(c.caso_id)} className="whitespace-nowrap"
        data-testid="seguimiento-fila-cerrar" aria-label={`Cerrar el caso ${c.numero_de_caso}`}>
        Cerrar el caso
      </Button>
    </span>
  );
}

export default function DisposicionesYSeguimiento({ onAbrirCaso, casoFiltrado = null, onQuitarCaso }: {
  onAbrirCaso?: (casoId: string) => void;
  /** «Ver compromisos» desde el Expediente: solo los de este caso. */
  casoFiltrado?: { casoId: string; numero: string } | null;
  onQuitarCaso?: () => void;
}) {
  // Multi-rol (HU-32b): basta con UNO de los roles de la sesión (`tieneRol` mira todos).
  const usuario = usuarioActual();
  const registra = ROLES_QUE_REGISTRAN.some((r) => tieneRol(usuario, r));
  const cierra = ROLES_QUE_CIERRAN.some((r) => tieneRol(usuario, r));
  const catalogos = useCatalogosOpcional();
  const [filtros, setFiltrosDeLaPantalla] = useState<Filtros>(() => filtrosIniciales('seguimiento', SIN_FILTROS));
  const [pagina, setPagina] = useState(1);
  const casoId = casoFiltrado?.casoId ?? null;
  // Cualquier cambio de filtro, o de caso desde el expediente, vuelve a la página 1.
  const setFiltros = useCallback((cambio: Filtros | ((f: Filtros) => Filtros)) => {
    setFiltrosDeLaPantalla(cambio);
    setPagina(1);
  }, []);
  const [casoPrevio, setCasoPrevio] = useState(casoId);
  if (casoPrevio !== casoId) {
    setCasoPrevio(casoId);
    setPagina(1);
  }
  const [lista, setLista] = useState<ListaDeCompromisos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [falloDeEnvio, setFalloDeEnvio] = useState<Fallo | null>(null);
  const [exito, setExito] = useState<Exito | null>(null);
  const [cambiando, setCambiando] = useState<string | null>(null);
  const [revirtiendo, setRevirtiendo] = useState<Compromiso | null>(null);
  const [registrando, setRegistrando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const leida = await listarCompromisos(aConsulta(filtros, casoId, pagina));
      // Tras un cambio de estado la última página puede quedar vacía (la fila pasó a
      // «Cumplida» y se oculta): se va a la última página que sí tiene filas.
      if (!leida.compromisos.length && leida.total > 0 && pagina > 1) {
        setPagina(paginasDe(leida.total));
        return;
      }
      setLista(leida);
      setFallo(null);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setCargando(false);
    }
  }, [filtros, casoId, pagina]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { guardarFiltros('seguimiento', filtros); }, [filtros]);

  // El servidor ya filtró, ordenó y paginó: la pantalla pinta lo que llega.
  const filas = useMemo(() => lista?.compromisos ?? [], [lista]);

  const cambiar = async (c: Compromiso, estado: string) => {
    if (!estado || estado === c.estado) return;
    setFalloDeEnvio(null);
    setExito(null);
    setCambiando(c.compromiso_id);
    try {
      await cambiarEstadoDeCompromiso(c.caso_id, c.compromiso_id, { estado });
      setExito({ titulo: `${c.numero_de_caso} · ${etiquetaDelCompromiso(estado)}`, texto: c.descripcion });
    } catch (e) {
      // El selector vuelve al valor del servidor: la lista se relee igual.
      setFalloDeEnvio(aFallo(e));
    } finally {
      setCambiando(null);
      cargar();
    }
  };

  const alRevertir = (c: Compromiso, estado: string) => {
    setRevirtiendo(null);
    setExito({ titulo: `${c.numero_de_caso} · ${etiquetaDelCompromiso(estado)}`, texto: c.descripcion });
    cargar();
  };

  const alRegistrar = (r: DisposicionRegistrada) => {
    setRegistrando(false);
    setExito({ titulo: 'Disposición registrada', texto: r.aviso, tono: r.aviso ? 'warning' : 'success' });
    cargar();
  };

  const celda = (r: FilaDeTabla, { key }: ColumnaDeTabla) => {
    const c = r as unknown as Compromiso;
    if (key === 'caso') return celdaDelCaso(c, cierra, onAbrirCaso);
    if (key === 'clase') return <StatusBadge tone={tonoDeLaClase(c.tipo)}>{c.texto_de_clase}</StatusBadge>;
    if (key === 'que') {
      return (
        <div>
          <p className="text-body text-ink line-clamp-2" title={c.descripcion}>{c.descripcion}</p>
          {(c.autoridad || c.texto_de_registro) && (
            <p className="text-meta text-ink-soft">
              {c.autoridad && <span data-testid="seguimiento-fila-autoridad">{c.autoridad}</span>}
              {c.autoridad && c.texto_de_registro && ' · '}
              {c.texto_de_registro && <span data-testid="seguimiento-fila-registrada-por">{c.texto_de_registro}</span>}
            </p>
          )}
        </div>
      );
    }
    if (key === 'responsable') return c.responsable;
    if (key === 'plazo') {
      return (
        <div>
          <Mono>{comoFecha(c.plazo)}</Mono>
          {c.texto_de_vencimiento && (
            // HU-27-C2 / RNF-11: texto e ícono, no solo color.
            <p className="text-meta text-rose-700 inline-flex items-center gap-1 whitespace-nowrap">
              <AlertTriangle size={12} aria-hidden="true" /> {c.texto_de_vencimiento}
            </p>
          )}
        </div>
      );
    }
    if (key === 'estado') {
      if (c.puede_revertir) {
        return (
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusBadge tone={tonoDelCompromiso(c.estado)}>{c.texto_del_estado}</StatusBadge>
            <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => setRevirtiendo(c)} data-testid="seguimiento-fila-revertir"
              aria-label={`Revertir «${c.texto_del_estado}» de ${c.numero_de_caso}`}>
              Revertir
            </Button>
          </span>
        );
      }
      if (c.puede_cambiar_estado && c.estados_siguientes.length) {
        const opciones: OpcionDeSelect[] = [
          { value: c.estado, label: c.texto_del_estado },
          ...c.estados_siguientes.map((e) => ({ value: e, label: etiquetaDelCompromiso(e) })),
        ];
        return (
          <Select sinVacio compacto options={opciones} value={c.estado} disabled={cambiando === c.compromiso_id}
            aria-label={`Estado del compromiso de ${c.numero_de_caso}: ${c.descripcion}`}
            onChange={(e) => { cambiar(c, String(e.target.value ?? '')); }} />
        );
      }
      return <StatusBadge tone={tonoDelCompromiso(c.estado)}>{c.texto_del_estado}</StatusBadge>;
    }
    return null;
  };

  const hayFiltros = filtros.clase || filtros.estado || filtros.ver_cumplidas || filtros.pais || casoFiltrado;
  const quitarFiltros = () => { setFiltros(SIN_FILTROS); onQuitarCaso?.(); };
  // Lo que se anuncia con la tarjeta plegada: cada filtro distinto del valor con que nace la
  // pantalla («Todas», «Todos», «Todos los de tu ámbito», sin cumplidas) y el caso del Expediente.
  const filtrosActivos = [filtros.clase, filtros.estado, filtros.pais, filtros.ver_cumplidas, casoFiltrado]
    .filter(Boolean).length;

  let cuerpo;
  if (fallo?.estado === 403) {
    cuerpo = (
      <EmptyState icon={ShieldAlert} title="Esta pantalla es del Comité, Asuntos Regulatorios y los Representantes de País"
        description={fallo.message} data-testid="seguimiento-sin-permiso" />
    );
  } else if (fallo) {
    cuerpo = (
      <div className="p-5">
        <Alert tone="danger" title="No se pudo cargar la lista de compromisos" detalleTecnico={fallo.detalleTecnico} data-testid="seguimiento-error">
          {fallo.message}
          <div className="mt-2"><Button size="sm" variant="secondary" icon={RotateCw} onClick={() => { cargar(); }}>Reintentar</Button></div>
        </Alert>
      </div>
    );
  } else if (!lista) {
    cuerpo = <p className="p-5 text-body text-ink-soft" data-testid="seguimiento-cargando">Cargando los compromisos…</p>;
  } else {
    cuerpo = (
      <DataTable columns={COLUMNAS} rows={filas.map((c) => ({ ...c, id: c.compromiso_id }))} renderCell={celda} sinMarco
        data-testid="seguimiento-tabla" testidDeFila="seguimiento-fila" regionDesplazable="Compromisos"
        empty={(
          <EmptyState icon={ListChecks} title="No hay compromisos abiertos en tu ámbito" data-testid="seguimiento-vacio"
            action={hayFiltros ? (
              <Button variant="ghost" icon={X} onClick={quitarFiltros} data-testid="seguimiento-quitar-filtros">Quitar filtros</Button>
            ) : undefined} />
        )} />
    );
  }
  const pie = lista && !fallo && lista.total > 0 && (
    <div className="px-5 py-3 border-t border-hairline">
      <Paginacion pagina={lista.pagina ?? pagina} total={lista.total} enEstaPagina={filas.length}
        porPagina={lista.por_pagina || POR_PAGINA_DE_LISTADOS} onPagina={setPagina}
        etiqueta="Paginación de los compromisos" sustantivo={['compromiso', 'compromisos']}
        data-testid="seguimiento-paginacion" />
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="Seguimiento"
        hu="HU-27 · HU-25"
        title="Disposiciones y seguimiento"
        subtitle="Las acciones del Comité y las disposiciones de la autoridad de tu ámbito, en una sola lista: lo que vence antes va primero."
        icon={ListChecks}
        actions={registra ? (
          <Button icon={FilePlus2} onClick={() => setRegistrando(true)} data-testid="seguimiento-registrar-disposicion">
            Registrar disposición
          </Button>
        ) : undefined}
      />

      {lista && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <KpiCard label="Abiertos" value={lista.abiertos} data-testid="seguimiento-abiertos" />
          <KpiCard label="Vencidos" value={lista.vencidos} tone={lista.vencidos ? 'danger' : undefined} data-testid="seguimiento-vencidos" />
        </div>
      )}

      {exito && (
        <div className="mb-3">
          <Alert tone={exito.tono ?? 'success'} title={exito.titulo} data-testid="seguimiento-exito" onCerrar={() => setExito(null)}>
            {exito.texto}
          </Alert>
        </div>
      )}
      {falloDeEnvio && (
        <div className="mb-3">
          <Alert tone="warning" title="No se cambió el estado" detalleTecnico={falloDeEnvio.detalleTecnico}
            data-testid="seguimiento-error-envio" onCerrar={() => setFalloDeEnvio(null)}>
            {falloDeEnvio.message}
          </Alert>
        </div>
      )}

      <FiltrosColapsables className="mb-4" activos={filtrosActivos} data-testid="seguimiento-filtros">
        {/* Filtros como en las bandejas (Comité, Regulatorio): un `Select` con nombre accesible
            por filtro, en rejilla; la casilla «Ver cumplidas» en la misma fila. Plegable y
            abierta al entrar (operador, 5-oct-2026). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <Select sinVacio options={CLASES} value={filtros.clase} data-testid="seguimiento-filtro-clase" aria-label="Filtrar por clase"
            onChange={(e) => setFiltros((f) => ({ ...f, clase: String(e.target.value ?? '') }))} />
          <Select sinVacio options={ESTADOS} value={filtros.estado} data-testid="seguimiento-filtro-estado" aria-label="Filtrar por estado"
            onChange={(e) => setFiltros((f) => ({ ...f, estado: String(e.target.value ?? '') }))} />
          <SelectorDePais sinVacio buscable antes={TODOS_LOS_PAISES} paises={catalogos?.PAISES ?? []} value={filtros.pais} data-testid="seguimiento-filtro-pais" aria-label="Filtrar por país"
            onChange={(e) => setFiltros((f) => ({ ...f, pais: String(e.target.value ?? '') }))} />
          <Checkbox checked={filtros.ver_cumplidas === 'si'} label="Ver cumplidas" data-testid="seguimiento-filtro-ver-cumplidas"
            onChange={() => setFiltros((f) => ({ ...f, ver_cumplidas: f.ver_cumplidas === 'si' ? '' : 'si' }))} />
        </div>
        {casoFiltrado && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-body">
            <span className="text-ink-soft">Solo el caso</span>
            <Mono>{casoFiltrado.numero}</Mono>
            {onQuitarCaso && (
              <Button size="sm" variant="ghost" icon={X} onClick={onQuitarCaso} data-testid="seguimiento-quitar-caso"
                aria-label={`Ver los compromisos de todos los casos, no solo ${casoFiltrado.numero}`}>
                Todos los casos
              </Button>
            )}
          </div>
        )}
      </FiltrosColapsables>

      <Card pad={false}>
        {cargando && lista && <p className="sr-only" role="status">Actualizando la lista…</p>}
        {cuerpo}
        {pie}
      </Card>

      <ModalDeReversion compromiso={revirtiendo} onCerrar={() => setRevirtiendo(null)} onRevertida={alRevertir} />
      {registra && (
        <RegistrarDisposicion abierto={registrando} onCerrar={() => setRegistrando(false)} onRegistrada={alRegistrar} />
      )}
    </>
  );
}
